// pickStepGen.ts — Generate 6-phase crane pick animation steps for the cast layout.
// Each panel pick = 6 steps: move crane, swing to panel, lift, swing to wall,
// set & brace (critical field step — crane holds load while crew installs braces),
// release rigging.

import type { CastPlacedPanel } from './castLayout';
import type { PickStep } from '../types/panelvar';
import type { InstalledPosition } from './installedPositions';

export const STEP_DURATION_MS = 600; // base ms per step; divided by playback speed

export function generatePickSteps(
  placed: CastPlacedPanel[],
  craneX: number,
  craneY: number,
  installedPositions: Map<string, InstalledPosition>,
): PickStep[] {
  const steps: PickStep[] = [];
  let stepIndex = 0;

  for (const pp of placed) {
    const inst = installedPositions.get(pp.panel.id);
    const destX = inst?.x ?? pp.cx;
    const destY = inst?.y ?? pp.cy;

    const pickBearing = Math.atan2(pp.cy - craneY, pp.cx - craneX);
    const destBearing = Math.atan2(destY - craneY, destX - craneX);
    const pickRadius  = Math.hypot(pp.cx - craneX, pp.cy - craneY);
    const destRadius  = Math.hypot(destX - craneX, destY - craneY);

    const braceCount = pp.panel.bpvar.filter(s => s.enabled).length;
    const mark = pp.panel.mpvar.panelMark || pp.panel.id.slice(-4);

    // Phase 1 — Hook up: crane rotates to face panel; rigging descends; panel flat
    steps.push({
      stepIndex: stepIndex++, panelId: pp.panel.id, phase: 1,
      craneArmAngleRad: pickBearing,
      craneArmLengthIn: pickRadius,
      panelCx: pp.cx, panelCy: pp.cy,
      panelTiltAngle: 0,        panelElevFt: 0,
      description: `${mark} — Hook up`,
    });

    // Phase 2 — Tilt-up: panel pivots from flat to vertical; bottom edge stays on slab
    steps.push({
      stepIndex: stepIndex++, panelId: pp.panel.id, phase: 2,
      craneArmAngleRad: pickBearing,
      craneArmLengthIn: pickRadius,
      panelCx: pp.cx, panelCy: pp.cy,
      panelTiltAngle: Math.PI / 2, panelElevFt: 0,
      description: `${mark} — Tilt up`,
    });

    // Phase 3 — Lift: panel rises ~4 ft clearance (bottom off slab)
    steps.push({
      stepIndex: stepIndex++, panelId: pp.panel.id, phase: 3,
      craneArmAngleRad: pickBearing,
      craneArmLengthIn: pickRadius,
      panelCx: pp.cx, panelCy: pp.cy,
      panelTiltAngle: Math.PI / 2, panelElevFt: 4,
      description: `${mark} — Lift`,
    });

    // Phase 4 — Swing: crane rotates; panel travels to wall position
    steps.push({
      stepIndex: stepIndex++, panelId: pp.panel.id, phase: 4,
      craneArmAngleRad: destBearing,
      craneArmLengthIn: destRadius,
      panelCx: destX, panelCy: destY,
      panelTiltAngle: Math.PI / 2, panelElevFt: 4,
      description: `${mark} — Swing to wall`,
    });

    // Phase 5 — Set & brace: panel lowered to footing; crew installs braces; crane holds load
    steps.push({
      stepIndex: stepIndex++, panelId: pp.panel.id, phase: 5,
      craneArmAngleRad: destBearing,
      craneArmLengthIn: destRadius,
      panelCx: destX, panelCy: destY,
      panelTiltAngle: Math.PI / 2, panelElevFt: 0,
      description: `${mark} — Set & brace (${braceCount} brace${braceCount !== 1 ? 's' : ''})`,
    });

    // Phase 6 — Release: rigging disconnected; panel self-supporting on braces
    steps.push({
      stepIndex: stepIndex++, panelId: pp.panel.id, phase: 6,
      craneArmAngleRad: destBearing,
      craneArmLengthIn: destRadius,
      panelCx: destX, panelCy: destY,
      panelTiltAngle: Math.PI / 2, panelElevFt: 0,
      description: `${mark} — Release rigging`,
    });
  }

  return steps;
}
